import { useEffect, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { authClient } from "./auth-client";

const CenteredScreen = ({ children }: { children: ReactNode }) => (
  <div className="flex min-h-svh items-center justify-center bg-background p-6">
    {children}
  </div>
);

/**
 * Gates the app on auth readiness. msal mode: initializes MSAL, processes the
 * redirect response, and shows a sign-in card until an account exists.
 * local mode: renders children immediately.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    authClient
      .initialize()
      .then(() => setReady(true))
      .catch((error: unknown) => {
        setInitError(error instanceof Error ? error.message : String(error));
      });
  }, []);

  if (initError) {
    return (
      <CenteredScreen>
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Sign-in unavailable</CardTitle>
            <CardDescription>{initError}</CardDescription>
          </CardHeader>
        </Card>
      </CenteredScreen>
    );
  }

  if (!ready) {
    return (
      <CenteredScreen>
        <p className="text-muted-foreground text-sm">Loading…</p>
      </CenteredScreen>
    );
  }

  if (!authClient.isAuthenticated()) {
    return (
      <CenteredScreen>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Moreta Admin</CardTitle>
            <CardDescription>
              Sign in with your Moreta Microsoft account to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => void authClient.login()}>
              Sign in with Microsoft
            </Button>
          </CardContent>
        </Card>
      </CenteredScreen>
    );
  }

  return <>{children}</>;
}
